<script setup lang="ts">
import { computed } from 'vue'
import { Handle, Position } from '@vue-flow/core'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem
} from '@/components/ui/dropdown-menu'
import { MoreHorizontal, Pencil, Trash2, Volume2, Monitor, Play, Pause, Repeat, Copy, Loader2 } from 'lucide-vue-next'
import NodeFrame from './NodeFrame.vue'
import type { MusicNode } from '@/stores/musicNodes'

interface Props {
  node: MusicNode
  isPlaying?: boolean
  currentTime?: number
  duration?: number
  isMobile?: boolean
  displays: Array<{ id: string; name: string }>
  defaultPlaybackTarget: string
}

const props = defineProps<Props>()

// Compute progress percentage for spectrogram animation offset
const playProgress = computed(() => {
  if (!props.duration || props.duration === 0) return 0
  return ((props.currentTime || 0) / props.duration) * 100
})

// Check if node is uploading
const isUploading = computed(() => props.node.isUploading ?? false)

const emit = defineEmits<{
  togglePlayback: [node: MusicNode]
  edit: [node: MusicNode]
  delete: [node: MusicNode]
  duplicate: [node: MusicNode]
  toggleLoop: [node: MusicNode]
  updateVolume: [nodeId: string, volume: number]
  updatePlaybackTarget: [nodeId: string, target: string]
}>()

// Expose Position enum values for template
const handlePositions = {
  left: Position.Left
}

const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
</script>

<template>
  <!-- Source handle (left) - for connecting to scenes -->
  <Handle 
    type="source" 
    :position="handlePositions.left" 
    id="audio-out"
    class="audio-handle"
  />
  
  <div 
    class="music-node-wrapper"
    :class="{ 
      'music-node-mobile': isMobile,
      'music-node-playing': isPlaying,
      'music-node-uploading': isUploading
    }"
  >
    <NodeFrame 
      :id="`music-${node.id}`"
      variant="music"
      :is-active="isPlaying"
      :show-glow="!isUploading"
    >
      <!-- Spectrogram glow effect (when playing) -->
      <div v-if="isPlaying" class="spectrogram-container" :style="{ '--progress': playProgress }">
        <div class="spectrogram-bar" style="--delay: 0s; --height: 60%"></div>
        <div class="spectrogram-bar" style="--delay: 0.2s; --height: 100%"></div>
        <div class="spectrogram-bar" style="--delay: 0.4s; --height: 40%"></div>
        <div class="spectrogram-bar" style="--delay: 0.1s; --height: 80%"></div>
        <div class="spectrogram-bar" style="--delay: 0.3s; --height: 50%"></div>
        <div class="spectrogram-bar" style="--delay: 0.5s; --height: 70%"></div>
        <div class="spectrogram-bar" style="--delay: 0.15s; --height: 90%"></div>
        <div class="spectrogram-bar" style="--delay: 0.35s; --height: 45%"></div>
      </div>
      
      <!-- Content -->
      <div class="music-node-content" @click.stop="!isUploading && emit('togglePlayback', node)">
        <div class="music-node-icon">
          <!-- Loading spinner when uploading -->
          <Loader2 
            v-if="isUploading" 
            class="h-5 w-5 text-blue-400 animate-spin" 
          />
          <!-- Cover art if available (not uploading) -->
          <img 
            v-else-if="node.coverUrl" 
            :src="node.coverUrl" 
            class="h-8 w-8 object-cover rounded"
            alt="Album cover"
          />
          <!-- Fallback icon (not uploading) -->
          <component 
            v-else
            :is="isPlaying ? Pause : Play" 
            class="h-5 w-5 text-blue-400" 
          />
          <!-- Play/pause overlay on cover (not when uploading) -->
          <div v-if="node.coverUrl && !isUploading" class="music-node-play-overlay">
            <component 
              :is="isPlaying ? Pause : Play" 
              class="h-4 w-4 text-white" 
            />
          </div>
        </div>
        
        <div class="music-node-info">
          <span class="music-node-name">{{ node.name }}</span>
          <span class="music-node-type">
            <template v-if="isUploading">Uploading...</template>
            <template v-else>{{ node.duration ? formatDuration(node.duration) : 'Sound' }}</template>
          </span>
        </div>
      </div>
      
      <!-- Menu button positioned at bottom right (hidden when uploading) -->
      <div v-if="!isUploading" class="music-node-menu-container">
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <Button
              variant="ghost"
              size="icon"
              class="music-node-menu"
              @click.stop
            >
              <MoreHorizontal class="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem @click.stop="emit('edit', node)">
              <Pencil class="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem @click.stop="emit('duplicate', node)">
              <Copy class="h-4 w-4 mr-2" />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel class="text-xs text-muted-foreground">Volume</DropdownMenuLabel>
            <div class="px-2 py-1.5" @click.stop>
              <div class="flex items-center gap-2">
                <Volume2 class="h-3 w-3 text-muted-foreground flex-shrink-0" />
                <Slider
                  :model-value="[node.volume * 100]"
                  :max="100"
                  :step="1"
                  class="flex-1"
                  @update:model-value="(v) => v && emit('updateVolume', node.id, v[0] / 100)"
                />
                <span class="text-xs text-muted-foreground w-8">{{ Math.round(node.volume * 100) }}%</span>
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem @click.stop="emit('toggleLoop', node)">
              <Repeat class="h-4 w-4 mr-2" :class="{ 'text-primary': node.loop !== false }" />
              {{ node.loop !== false ? 'Loop (On)' : 'Loop (Off)' }}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel class="text-xs text-muted-foreground">Play on</DropdownMenuLabel>
            <DropdownMenuRadioGroup 
              :model-value="node.playback_target || defaultPlaybackTarget"
              @update:model-value="(v) => emit('updatePlaybackTarget', node.id, v)"
            >
              <DropdownMenuRadioItem 
                v-for="display in displays" 
                :key="display.id" 
                :value="display.id"
                @click.stop
              >
                <Monitor class="h-4 w-4 mr-2" />
                {{ display.name }}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              @click.stop="emit('delete', node)"
              class="text-destructive focus:text-destructive"
            >
              <Trash2 class="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </NodeFrame>
  </div>
</template>

<style scoped>
.music-node-wrapper {
  width: 200px;
  height: 64px;
  cursor: default;
  transition: transform 200ms ease;
}

.music-node-mobile {
  width: 160px;
  height: 56px;
}

.music-node-wrapper:hover {
  transform: translateY(-2px);
}

/* Disable hover effects on mobile */
@media (hover: none) {
  .music-node-wrapper:hover {
    transform: none;
  }
}

/* Spectrogram effect */
.spectrogram-container {
  position: absolute;
  inset: -10px;
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  padding: 0 16px;
  pointer-events: none;
  z-index: -1;
  filter: blur(8px);
  opacity: 0.5;
}

.spectrogram-bar {
  width: 8px;
  background: var(--gold-gradient-light);
  border-radius: 4px;
  animation: spectrogram-bounce 0.8s ease-in-out infinite;
  animation-delay: var(--delay, 0s);
  height: var(--height, 50%);
}

@keyframes spectrogram-bounce {
  0%, 100% {
    transform: scaleY(0.3);
    opacity: 0.5;
  }
  50% {
    transform: scaleY(1);
    opacity: 1;
  }
}

/* Content */
.music-node-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  height: 100%;
  cursor: pointer;
}

.music-node-mobile .music-node-content {
  gap: 8px;
  padding: 8px 12px;
}

.music-node-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(96, 165, 250, 0.15);
  border: 1px solid rgba(96, 165, 250, 0.3);
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}

.music-node-icon img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: 4px;
}

.music-node-play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  opacity: 0;
  transition: opacity 150ms ease;
}

.music-node-content:hover .music-node-play-overlay {
  opacity: 1;
}

.music-node-mobile .music-node-icon {
  width: 32px;
  height: 32px;
}

.music-node-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.music-node-name {
  font-weight: 600;
  font-size: 13px;
  color: hsl(var(--foreground));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.music-node-mobile .music-node-name {
  font-size: 12px;
}

.music-node-type {
  font-size: 11px;
  color: hsl(var(--muted-foreground));
}

.music-node-menu-container {
  position: absolute;
  bottom: 4px;
  right: 8px;
}

.music-node-menu {
  width: 24px;
  height: 24px;
  opacity: 0;
  transition: opacity 150ms ease;
}

.music-node-wrapper:hover .music-node-menu,
.music-node-menu:focus,
.music-node-menu[data-state="open"] {
  opacity: 1;
}

/* Uploading state */
.music-node-uploading {
  opacity: 0.7;
  pointer-events: none;
}

.music-node-uploading .music-node-content {
  pointer-events: auto;
  cursor: default;
}

/* Audio handle styling */
.audio-handle {
  background: hsl(var(--primary)) !important;
}

:deep(.vue-flow__handle.audio-handle) {
  width: 8px;
  height: 8px;
  background: hsl(var(--primary));
}
</style>
